'use client';
import {
  Container,
  SearchFilterSelect,
  type SearchFilterOption
} from '@paction-2.0/ui';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef } from 'react';
import { useSearchBreedOptions } from './useSearchBreedOptions';
import type { NormalizedCatalogSearchFilters } from '@/features/catalog';
import { useCollapsibleSearchHeader } from './useCollapsibleSearchHeader';
import { useMobileMenu } from '@/lib/contexts/MobileMenuContext';
import { useAuth } from '@/lib/auth/AuthProvider';
import styles from './styles/collapsibleSearchHeader.module.scss';

type BreedOptionMeta = { isTargeting: boolean; photoUrl: string | null };

function BreedOptionLabel({
  option
}: {
  option: SearchFilterOption<BreedOptionMeta>;
}) {
  const photoUrl = option.meta?.photoUrl;
  return (
    <span className={styles.breedOption}>
      {photoUrl ? (
        <Image
          src={photoUrl}
          alt=""
          width={32}
          height={32}
          className={styles.breedOptionPhoto}
          unoptimized
        />
      ) : (
        <span className={styles.breedOptionPhotoPlaceholder} aria-hidden="true">
          🐾
        </span>
      )}
      <span>{option.label}</span>
    </span>
  );
}

function CollapseChevronIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      role="presentation"
      focusable="false"
      aria-hidden="true"
    >
      <path
        d="M9 6l6 6-6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AvatarMenuButton({ onClick }: { onClick: () => void }) {
  const { session } = useAuth();
  const profile = session?.profile;

  const firstName =
    (profile as { firstName?: string } | undefined)?.firstName ||
    (profile as { account?: { firstName?: string } } | undefined)?.account
      ?.firstName ||
    null;
  const lastName =
    (profile as { lastName?: string } | undefined)?.lastName ||
    (profile as { account?: { lastName?: string } } | undefined)?.account
      ?.lastName ||
    null;
  const photoUrl =
    (profile as { account?: { photoUrl?: string } } | undefined)?.account
      ?.photoUrl ||
    (profile as { photoUrl?: string } | undefined)?.photoUrl ||
    null;

  const initials =
    `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || null;

  return (
    <button
      type="button"
      className={styles.menuButton}
      onClick={onClick}
      aria-label="Open menu"
    >
      {photoUrl ? (
        <Image
          src={photoUrl}
          alt=""
          width={36}
          height={36}
          className={styles.menuAvatar}
          unoptimized
        />
      ) : initials ? (
        <span className={styles.menuAvatarInitials}>{initials}</span>
      ) : (
        <svg
          viewBox="0 0 24 24"
          role="presentation"
          focusable="false"
          aria-hidden="true"
        >
          <path
            d="M4 7h16M4 12h16M4 17h16"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      )}
    </button>
  );
}
type CollapsibleSearchHeaderProps = {
  selectedBreedLabel?: string;
  filters: NormalizedCatalogSearchFilters;
  onApplyFilters: (
    partial: Partial<Omit<NormalizedCatalogSearchFilters, 'page'>>
  ) => void;
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
};

export function CollapsibleSearchHeader({
  selectedBreedLabel,
  filters,
  onApplyFilters,
  isExpanded,
  onExpandedChange
}: CollapsibleSearchHeaderProps) {
  const onMenuClick = useMobileMenu();
  const headerRef = useRef<HTMLElement>(null);
  const breeds = useSearchBreedOptions();
  const {
    expandedPanelId,
    breedLabelId,
    provinceLabelId,
    cityLabelId,
    breedOptions,
    provinceOptions,
    cityOptions,
    buttonLabel,
    handleBreedSelect,
    handleProvinceSelect,
    handleCitySelect,
    handleExpand,
    handleCollapse,
    breedValue,
    provinceValue,
    cityValue,
    showCityDropdown
  } = useCollapsibleSearchHeader({
    breeds,
    selectedBreedLabel,
    filters,
    onApplyFilters,
    onExpandedChange
  });

  useEffect(() => {
    if (!isExpanded) return;
    function onPointerDown(event: PointerEvent) {
      if (
        headerRef.current &&
        !headerRef.current.contains(event.target as Node)
      ) {
        handleCollapse();
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [isExpanded, handleCollapse]);

  return (
    <header
      ref={headerRef}
      className={`${styles.header} ${isExpanded ? styles.headerExpanded : ''}`}
    >
      {!isExpanded ? (
        <Container maxWidth="full" padding="none" className={styles.panelShell}>
          <div className={styles.compactBar}>
            <div className={styles.compactBarLeft}>
              <Link href="/" className={styles.logoLink}>
                <Image
                  src="/brand/paction-logo-cropped.svg"
                  alt="Paction logo"
                  width={64}
                  height={64}
                  className={styles.logoMark}
                  priority
                />
              </Link>
            </div>
            <div className={styles.compactBarCenter}>
              <button
                type="button"
                className={styles.compactButton}
                onClick={handleExpand}
                aria-expanded={isExpanded}
                aria-controls={expandedPanelId}
              >
                <span className={styles.compactButtonIcon} aria-hidden="true">
                  <Image
                    src="/icons/search-icon.svg"
                    alt=""
                    width={18}
                    height={18}
                    className={styles.searchIcon}
                  />
                </span>
                <span className={styles.compactButtonLabel}>{buttonLabel}</span>
              </button>
            </div>
            <div className={styles.compactBarRight}>
              {onMenuClick && <AvatarMenuButton onClick={onMenuClick} />}
            </div>
          </div>
        </Container>
      ) : (
        <Container
          id={expandedPanelId}
          maxWidth="full"
          padding="none"
          className={`${styles.panelShell} ${styles.expandedPanel}`}
        >
          <div className={styles.expandedHeader}>
            <p className={styles.expandedTitle}>
              Find a puppy
              <Image
                src="/icons/paw-prints-icon.svg"
                alt="paw prints"
                width={24}
                height={24}
                className={styles.pawIcon}
                aria-hidden="true"
              />
            </p>
            <button
              type="button"
              aria-label="Collapse search"
              className={styles.collapseButton}
              onClick={handleCollapse}
            >
              <CollapseChevronIcon />
            </button>
          </div>
          <div className={styles.selectGrid}>
            <span id={breedLabelId} className={styles.srOnly}>
              Breed
            </span>
            <span id={provinceLabelId} className={styles.srOnly}>
              Province
            </span>
            <span id={cityLabelId} className={styles.srOnly}>
              City
            </span>
            <SearchFilterSelect
              className={styles.selectItem}
              options={breedOptions}
              value={breedValue}
              placeholder="Select a breed"
              ariaLabelledby={breedLabelId}
              emptyStateLabel="No breeds found"
              renderOptionLabel={(option) => (
                <BreedOptionLabel option={option} />
              )}
              onSelect={(nextValue) => handleBreedSelect(nextValue)}
              onClear={() => handleBreedSelect(undefined)}
            />
            <SearchFilterSelect
              className={styles.selectItem}
              options={provinceOptions}
              value={provinceValue}
              placeholder="Select a province"
              ariaLabelledby={provinceLabelId}
              searchEnabled={false}
              onSelect={(nextValue) => handleProvinceSelect(nextValue)}
              onClear={() => handleProvinceSelect(undefined)}
            />
            {showCityDropdown && (
              <SearchFilterSelect
                className={styles.selectItem}
                options={cityOptions}
                value={cityValue}
                placeholder="Select a city"
                ariaLabelledby={cityLabelId}
                searchEnabled
                emptyStateLabel="No cities found"
                onSelect={(nextValue) => handleCitySelect(nextValue)}
                onClear={() => handleCitySelect(undefined)}
              />
            )}
          </div>
        </Container>
      )}
    </header>
  );
}
